import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useApi } from './useApi';

/**
 * Custom hook for managing real-time Knowledge Base status updates via SSE
 * 
 * Features:
 * - WebSocket/SSE client connection management
 * - Real-time status update handling
 * - Automatic reconnection with exponential backoff
 * - Connection health monitoring
 * - Subscription management for specific parrots/files
 * 
 * Requirements: 1.4, 2.5, 4.5 - Real-time status updates for Knowledge Base operations
 */
export const useRealTimeStatusUpdates = ({
  subscriptions = {},
  autoConnect = true,
  onStatusUpdate = null,
  onConnectionChange = null,
  onError = null
} = {}) => {
  // Auth hook for getting tokens
  const { getToken } = useAuth();
  
  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected, connecting, connected, error
  const [lastUpdate, setLastUpdate] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  // Refs for managing connection lifecycle
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatTimeoutRef = useRef(null);
  const subscriptionsRef = useRef(subscriptions);

  // Configuration
  const maxReconnectAttempts = 10;
  const baseReconnectDelay = 1000; // 1 second
  const maxReconnectDelay = 30000; // 30 seconds
  const heartbeatTimeout = 60000; // 1 minute

  // Update subscriptions ref when props change
  useEffect(() => {
    subscriptionsRef.current = subscriptions;
  }, [subscriptions]);

  /**
   * Calculate reconnect delay with exponential backoff
   */
  const getReconnectDelay = useCallback((attempt) => {
    const delay = Math.min(
      baseReconnectDelay * Math.pow(2, attempt),
      maxReconnectDelay
    );
    // Add jitter to prevent thundering herd
    return delay + Math.random() * 1000;
  }, []);

  /**
   * Build SSE URL with subscription parameters
   */
  const buildSSEUrl = useCallback(async () => {
    const baseUrl = '/api/knowledge-base/status-updates';
    const params = new URLSearchParams();

    // Add authentication token for SSE (EventSource can't send custom headers)
    try {
      const token = await getToken();
      if (token) {
        params.append('token', token);
      }
    } catch (error) {
      console.error('Failed to get auth token for SSE:', error);
      throw error;
    }

    const subs = subscriptionsRef.current;

    if (subs.parrotIds && subs.parrotIds.length > 0) {
      params.append('parrot_ids', subs.parrotIds.join(','));
    }

    if (subs.fileIds && subs.fileIds.length > 0) {
      params.append('file_ids', subs.fileIds.join(','));
    }

    if (subs.sourceTypes && subs.sourceTypes.length > 0) {
      params.append('source_types', subs.sourceTypes.join(','));
    }

    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }, [getToken]);

  /**
   * Handle incoming SSE messages
   */
  const handleMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);
      setLastUpdate({
        ...data,
        receivedAt: new Date().toISOString()
      });

      // Reset heartbeat timeout
      if (heartbeatTimeoutRef.current) {
        clearTimeout(heartbeatTimeoutRef.current);
      }
      heartbeatTimeoutRef.current = setTimeout(() => {
        console.warn('SSE heartbeat timeout - connection may be stale');
        disconnect();
        if (autoConnect) {
          reconnect();
        }
      }, heartbeatTimeout);

      // Call status update handler
      if (onStatusUpdate) {
        onStatusUpdate(data, event.type);
      }

    } catch (error) {
      console.error('Failed to parse SSE message:', error);
      if (onError) {
        onError(new Error(`Failed to parse status update: ${error.message}`));
      }
    }
  }, [onStatusUpdate, onError, autoConnect]);

  /**
   * Handle SSE connection events
   */
  const handleOpen = useCallback(() => {
    // Knowledge Base status SSE connection established
    setIsConnected(true);
    setConnectionStatus('connected');
    setConnectionError(null);
    setReconnectAttempts(0);

    if (onConnectionChange) {
      onConnectionChange('connected');
    }
  }, [onConnectionChange]);

  const handleError = useCallback((error) => {
    console.error('Knowledge Base status SSE error:', error);
    setIsConnected(false);
    setConnectionStatus('error');
    
    const errorMessage = error.message || 'Connection error occurred';
    setConnectionError(errorMessage);

    if (onError) {
      onError(new Error(errorMessage));
    }

    if (onConnectionChange) {
      onConnectionChange('error', errorMessage);
    }

    // Attempt reconnection if auto-connect is enabled
    if (autoConnect && reconnectAttempts < maxReconnectAttempts) {
      reconnect();
    }
  }, [onError, onConnectionChange, autoConnect, reconnectAttempts]);

  /**
   * Connect to SSE endpoint
   */
  const connect = useCallback(async () => {
    if (eventSourceRef.current) {
      return; // Already connected or connecting
    }

    try {
      setConnectionStatus('connecting');
      setConnectionError(null);

      const url = await buildSSEUrl();
      // Connecting to Knowledge Base status updates

      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      // Set up event listeners
      eventSource.addEventListener('open', handleOpen);
      eventSource.addEventListener('error', handleError);

      // Handle different message types
      eventSource.addEventListener('message', handleMessage);
      eventSource.addEventListener('connected', handleMessage);
      eventSource.addEventListener('initial_status', handleMessage);
      eventSource.addEventListener('status_update', handleMessage);
      eventSource.addEventListener('sync_started', handleMessage);
      eventSource.addEventListener('sync_completed', handleMessage);
      eventSource.addEventListener('heartbeat', handleMessage);

      // Handle connection state changes
      eventSource.addEventListener('error', () => {
        if (eventSource.readyState === EventSource.CLOSED) {
          setIsConnected(false);
          setConnectionStatus('disconnected');
          eventSourceRef.current = null;
        }
      });

    } catch (error) {
      console.error('Failed to create SSE connection:', error);
      setConnectionStatus('error');
      setConnectionError(error.message);
      
      if (onError) {
        onError(error);
      }
    }
  }, [buildSSEUrl, handleOpen, handleError, handleMessage, onError]);

  /**
   * Disconnect from SSE endpoint
   */
  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }

    setIsConnected(false);
    setConnectionStatus('disconnected');
    setConnectionError(null);

    if (onConnectionChange) {
      onConnectionChange('disconnected');
    }
  }, [onConnectionChange]);

  /**
   * Reconnect with exponential backoff
   */
  const reconnect = useCallback(() => {
    if (reconnectAttempts >= maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      setConnectionStatus('error');
      setConnectionError('Max reconnection attempts reached');
      return;
    }

    const delay = getReconnectDelay(reconnectAttempts);
    // Reconnecting to Knowledge Base status updates

    setReconnectAttempts(prev => prev + 1);

    reconnectTimeoutRef.current = setTimeout(() => {
      disconnect(); // Clean up any existing connection
      connect();
    }, delay);
  }, [reconnectAttempts, maxReconnectAttempts, getReconnectDelay, disconnect, connect]);

  /**
   * Update subscriptions and reconnect if needed
   */
  const updateSubscriptions = useCallback((newSubscriptions) => {
    const wasConnected = isConnected;
    
    // Update subscriptions
    subscriptionsRef.current = newSubscriptions;
    
    // Reconnect if we were connected
    if (wasConnected) {
      disconnect();
      setTimeout(connect, 100); // Small delay to ensure clean disconnect
    }
  }, [isConnected, disconnect, connect]);

  /**
   * Force reconnection
   */
  const forceReconnect = useCallback(() => {
    setReconnectAttempts(0);
    disconnect();
    setTimeout(connect, 100);
  }, [disconnect, connect]);

  // Auto-connect on mount if enabled
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    // Connection state
    isConnected,
    connectionStatus,
    connectionError,
    reconnectAttempts,
    lastUpdate,

    // Connection controls
    connect,
    disconnect,
    reconnect: forceReconnect,
    updateSubscriptions,

    // Connection info
    maxReconnectAttempts,
    canReconnect: reconnectAttempts < maxReconnectAttempts
  };
};

export default useRealTimeStatusUpdates;
